/**
 * Grading-scheme EXTRACTION (reading an uploaded scheme) — one job per exam.
 *
 * Structurally identical to `ms-extraction.job.ts`: one bounding-box pass over the uploaded files and the
 * whole task tree, then a fan-out over regions. Same reason for no `desired` (see there).
 *
 * It does NOT gate on the master solution. Extraction reads what the teacher uploaded; only GENERATION needs
 * a master solution to write a scheme against — which is also why these two extractions can now run at the
 * same time (see `ms-extraction.job.ts`): neither is an input to the other.
 */

import { defineJob } from '../registry.js';
import { gradingSchemeStep } from '../steps/grading-scheme.step.js';
import { taskTreeLockKey } from '../job.js';
import * as conditions from '../job-conditions.js';
import { runGradingSchemeStep } from '../../pipeline/grading-scheme-step.js';

export const gradingSchemeExtractionJob = defineJob({
  type: 'gsExtraction',

  /** One row, unconditionally — see `ms-extraction.job.ts` for why there is nothing to check first. */
  /** Nothing but the run — see `ms-extraction.job.ts`. */
  onStepStart: {},

  batch: {
    units: () => [{}],
  },

  /** Closes the grading-scheme step once no job of it is left for this exam — see `STEP_JOB_TYPES`. */
  onStepComplete: gradingSchemeStep,

  /** Its own key — same reasoning as `ms-extraction.job.ts`, including the structure-step caveat. */
  lockKey: examId => `${taskTreeLockKey(examId)}:gradingScheme`,

  ready: exam => {
    if (!conditions.hasGradingSchemeFile(exam)) return { verdict: 'drop', because: 'gsExtraction:no-file' };
    if (!conditions.structureConfirmed(exam)) {
      return { verdict: 'wait', because: 'gsExtraction:structure-unconfirmed' };
    }
    return 'run';
  },

  maxRuntimeMs: 20 * 60_000,
  maxAttempts: 3,

  handler: async examId => {
    const result = await runGradingSchemeStep(examId);
    if (result.skipped) return { skipped: true, note: 'no grading scheme uploaded' };
    if (!result.ok) throw new Error('Grading scheme extraction reported a failure');
    return { note: 'grading scheme extracted' };
  },
});
